import FormHeader from "@/components/booking/FormHeader.tsx";
import {useFormContext} from "react-hook-form";
import {z} from "zod";
import {BookingFormSchema} from "@/components/booking/form/BookingFormSchema.ts";
import React, {useContext, useImperativeHandle, useRef} from "react";
import {OscarContext, OscarContextType} from "@/components/context/OscarContext.tsx";
import {BookingCharge, OscarConfigModel} from "@/lib/oscar/ApiModels.ts";
import {calculateSurcharge, formatCents} from "@/lib/utils.ts";
import {BookingDuration, BookingDurationHelper} from "@/lib/enum/BookingDuration.ts";
import {DateTime} from "@easepick/datetime";
import {BookingKindHelper} from "@/lib/enum/BookingKind.ts";

export type PricingCalculatorRef = {
    getTotal: () => Promise<number|null>;
}

type Props = object;

export const PricingCalculator = React.forwardRef<PricingCalculatorRef, Props>((_props, ref) => {
    const { watch } = useFormContext<z.infer<typeof BookingFormSchema>>();
    const { appConfig } = useContext(OscarContext) as OscarContextType
    const lastTotal = useRef<number|null>(null);

    // Time Data
    const [startDate, endDate, duration] = watch(["startDate", "endDate", "duration"]);

    /// Bin Data
    const binSize = watch("binSize");
    const binType = watch("binType");
    const binKind = watch("binKind");

    // Extras
    const extraItems = watch("extras");

    // Distance Cost
    const distanceCharge = watch("distanceCharge");

    /**
     * Get last known total cost.
     */
    const getTotal = async () => {
        return lastTotal.current;
    };

    /**
     * Expose the getCardDetails function to the parent component.
     */
    useImperativeHandle(ref, () => ({
        getTotal
    }));

    // Base Cost
    if (!startDate || !endDate || !binSize || !binType) {
        lastTotal.current = null;
        return (
            <div className={"os-app-row os-flex os-flex-col os-gap-4"}>
                <FormHeader>Pricing</FormHeader>
                <div>Please select your {BookingKindHelper.getBookingKindSlug(binKind)} size and type for pricing.</div>
            </div>
        );
    }

    // Base costs
    const calcData = calculateCost(appConfig, new DateTime(startDate), new DateTime(endDate), duration, binSize, binType);
    const durationData = calcData.duration;
    const baseCost = calcData.baseCost;

    let additionalDayCost: BookingCharge | null = null;
    if (durationData.additionalDays > 0 && baseCost?.additionalDay) {
        additionalDayCost = {
            id: baseCost.additionalDay.id,
            name: durationData.additionalDays + "x " + baseCost?.additionalDay.name,
            cost: baseCost.additionalDay.cost * durationData.additionalDays,
        }
    }

    // Safety check
    if (!baseCost) {
        lastTotal.current = null;
        return (
            <div className={"os-app-row md:os-col-span-2"}>
                <FormHeader>Pricing</FormHeader>
                <div>Please select your {BookingKindHelper.getBookingKindSlug(binKind)} size and type for pricing.</div>
            </div>
        );
    }

    // Extras.
    let extraCosts: ExtraCostRow[] = [];
    if (appConfig?.bookingData?.extras != null && extraItems) {
        extraCosts = appConfig.bookingData.extras
            .filter((extra) => (extraItems[extra.id] ?? 0) > 0)
            .map((extra) => {
                return {
                    id: extra.id,
                    name: extraItems[extra.id] + "x " + extra.name,
                    cost: extra.cost * (extraItems[extra.id] ?? 0),
                };
            });
    }

    // Calculate the total cost
    let totalCost = baseCost.cost;
    if (additionalDayCost) {
        totalCost += additionalDayCost.cost;
    }
    if (distanceCharge) {
        totalCost += distanceCharge;
    }
    totalCost += extraCosts.reduce((acc, extra) => acc + extra.cost, 0);

    // Surcharge
    const surcharge = appConfig?.configData?.surcharge ?? 0;
    const surchargeCost = calculateSurcharge(totalCost, surcharge);
    totalCost += surchargeCost;

    lastTotal.current = totalCost;

    return (
        <div className={"os-app-row os-flex os-flex-col os-gap-4"}>
            <FormHeader>Pricing</FormHeader>

            <table className="os-table os-table-auto">
                <thead>
                <tr>
                    <th className="os-w-full os-text-left os-p-2 os-bg-gray-100 os-rounded-l">Item</th>
                    <th className="os-w-full os-text-center os-p-2 os-bg-gray-100 os-rounded-r">Price</th>
                </tr>
                </thead>
                <tbody>
                {baseCost && (
                    <tr>
                        <td className="os-p-2 os-text-left">{baseCost.name}</td>
                        <td className="os-p-2 os-text-right">{formatCents(baseCost.cost)}</td>
                    </tr>
                )}
                {additionalDayCost && (
                    <tr>
                        <td className="os-p-2 os-text-left">{additionalDayCost.name}</td>
                        <td className="os-p-2 os-text-right">{formatCents(additionalDayCost.cost)}</td>
                    </tr>
                )}
                {distanceCharge && (
                    <tr>
                        <td className="os-p-2 os-text-left">Distance Charge</td>
                        <td className="os-p-2 os-text-right">{formatCents(distanceCharge)}</td>
                    </tr>
                )}
                {extraCosts.map((extra) => (
                    <tr key={extra.id}>
                        <td className="os-p-2 os-text-left">{extra.name}</td>
                        <td className="os-p-2 os-text-right">{formatCents(extra.cost)}</td>
                    </tr>
                ))}
                </tbody>
                <tfoot>
                {surcharge > 0 && (
                    <tr>
                        <td className="os-p-2 os-text-right">Card Payment Surcharge ({surcharge}%)</td>
                        <td className="os-p-2 os-text-right">{formatCents(surchargeCost)}</td>
                    </tr>
                )}
                <tr>
                    <td className="os-p-2 os-text-right">GST</td>
                    <td className="os-p-2 os-text-right">{formatCents(totalCost / 11)}</td>
                </tr>
                <tr>
                    <td className="os-p-2 os-text-right">Total</td>
                    <td className="os-p-2 os-text-right">{formatCents(totalCost)}</td>
                </tr>
                </tfoot>
            </table>
        </div>
    );
});

/**
 * Find the base cost for the booking.
 */
function findBaseCost(appConfig: OscarConfigModel | null, duration: BookingDuration, binSize: string, binType: string) {
    if (!appConfig?.bookingData?.pricing) {
        return null;
    }

    if (!duration || !binSize || !binType) {
        return null;
    }

    // Duration key
    const durationKey = BookingDurationHelper.getTotalDays(duration) ?? 8;

    // Pricing key
    const priceKey = `${binType}-${binSize}-${durationKey}`;

    return appConfig.bookingData.pricing[priceKey];
}

/**
 * Calculate the duration of the booking.
 */
function calculateDuration(appConfig: OscarConfigModel | null, start: DateTime, end: DateTime, selectedDuration: BookingDuration | null): DurationData {
    const disabledDates = new Set<string>(appConfig?.configData?.disabledDates ?? []);

    // Move to the start of the day.
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    // Find the day numbers.
    let totalDays = 1;
    let billableDays = 1;
    let currentEnd = new DateTime(start);
    while (currentEnd < end) {
        // We ignore weekends.
        if (currentEnd.getDay() != 0 && currentEnd.getDay() != 6 && !disabledDates.has(currentEnd.format("YYYY-MM-DD"))) {
            billableDays++;
        }
        currentEnd.add(1, "day");
        totalDays++;
    }

    // Find the charge type.
    let chargeType = BookingDuration.SHORT;
    if (selectedDuration == BookingDuration.MONTH) {
        // Force the month duration.
        chargeType = BookingDuration.MONTH;
    } else if (billableDays > BookingDurationHelper.getTotalDays(BookingDuration.SHORT)!) {
        // Week
        chargeType = BookingDuration.WEEK;
    } else if (selectedDuration == BookingDuration.WEEK) {
        // Force the week duration.
        chargeType = BookingDuration.WEEK;
    }

    // Find the additional billable days.
    let additionalDays = 0;
    currentEnd = new DateTime(start);
    currentEnd.add(BookingDurationHelper.getBillableDays(chargeType), "day");
    while (currentEnd < end) {
        // We ignore weekends.
        if (currentEnd.getDay() != 0 && currentEnd.getDay() != 6 && !disabledDates.has(currentEnd.format("YYYY-MM-DD"))) {
            additionalDays++;
        }
        currentEnd = currentEnd.add(1, "day");
    }

    return {
        chargeType: chargeType,
        totalDays: totalDays,
        billableDays: billableDays,
        additionalDays: additionalDays,
    };
}

/**
 * Calculate the cost of the booking.
 *
 * @param appConfig
 * @param startDate
 * @param endDate
 * @param duration
 * @param binSize
 * @param binType
 */
function calculateCost(appConfig: OscarConfigModel | null, startDate: DateTime, endDate: DateTime, duration: BookingDuration | null, binSize: string, binType: string): CalculatedCost {
    let durationData = calculateDuration(appConfig , new DateTime(startDate), new DateTime(endDate), duration);
    let baseCost = findBaseCost(appConfig, durationData.chargeType, binSize, binType);

    // Try if we can add additional days?
    if (!baseCost && durationData.chargeType != BookingDuration.SHORT) {
        // There is no charge for this duration, try duration below this one.
        let types: BookingDuration[] = [];
        if (durationData.chargeType == BookingDuration.WEEK) {
            types = [BookingDuration.SHORT];
        } else if (durationData.chargeType == BookingDuration.MONTH) {
            types = [BookingDuration.WEEK, BookingDuration.SHORT];
        }

        for (const newType of types) {
            baseCost = findBaseCost(appConfig, newType, binSize, binType);
            if (baseCost) {
                // Found a line item!
                // Rebuild the booking duration.
                durationData = calculateDuration(appConfig , new DateTime(startDate), new DateTime(endDate), newType);
                break;
            }
        }
    }

    // Try if we can use a longer duration? Wont work for a month.
    if (!baseCost && durationData.chargeType != BookingDuration.MONTH) {
        // There is no charge for this duration, try duration below this one.
        let types: BookingDuration[] = [];
        if (durationData.chargeType == BookingDuration.SHORT) {
            types = [BookingDuration.WEEK, BookingDuration.MONTH];
        } else if (durationData.chargeType == BookingDuration.WEEK) {
            types = [BookingDuration.MONTH];
        }

        for (const newType of types) {
            baseCost = findBaseCost(appConfig, newType, binSize, binType);
            if (baseCost) {
                // Found a line item!
                // Rebuild the booking duration.
                durationData = calculateDuration(appConfig , new DateTime(startDate), new DateTime(endDate), newType);
                break;
            }
        }
    }

    return {
        duration: durationData,
        baseCost: baseCost,
    }
}

type ExtraCostRow = {
    id: string;
    name: string;
    cost: number;
}

type DurationData = {
    chargeType: BookingDuration;
    totalDays: number;
    billableDays: number;
    additionalDays: number;
}

type CalculatedCost = {
    duration: DurationData;
    baseCost: BookingCharge | null;
}